import { useEffect, useState } from "react";

// sessionStorage can throw (private-mode quotas, sandboxed embeds); when it
// does, the hook degrades to plain useState and the draft won't survive.
function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * String state persisted to sessionStorage, so half-typed form input survives
 * an accidental reload (but not a closed tab). An empty value deletes the
 * entry. When `key` changes (e.g. a draft scoped per platform tab), the hook
 * switches to that key's stored draft.
 */
export function useSessionDraft(key: string): [string, (next: string) => void] {
  const [value, setValue] = useState(() => read(key) ?? "");
  // Reset-on-key-change via render-time state compare (the React-docs pattern
  // for deriving state from props — no effect, so no flash of the old draft).
  const [prevKey, setPrevKey] = useState(key);
  if (prevKey !== key) {
    setPrevKey(key);
    setValue(read(key) ?? "");
  }
  useEffect(() => {
    try {
      if (value) sessionStorage.setItem(key, value);
      else sessionStorage.removeItem(key);
    } catch {
      // storage unavailable — behave like plain state
    }
  }, [key, value]);
  return [value, setValue];
}
